import { CUSTOM_ELEMENTS_SCHEMA, Component, ElementRef, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { IonFab, IonicModule, ModalController } from '@ionic/angular';
import { isNil } from 'lodash';
import { GoogleMap, MapType } from '@capacitor/google-maps';
import { ScreenOrientation } from '@capacitor/screen-orientation';
import { DmStoreService } from 'src/app/store/dm-store.service';
import { mapTypes, sendSuccessTodnwaveio } from 'src/app/app.utils';
import { Subscription } from 'rxjs';
import { GroupBindStoreService } from 'src/app/store/group-bind-store.service';
import { ApiService, HelperProvider } from 'src/app/providers';
import { DeviceBindStoreService } from 'src/app/store/device-bind-store.service';
import { LocaLstorageService } from 'src/app/store/localstorage.service';
import {
  BindModalResult,
  DeviceCardModalResult,
  DeviceFindModalResult,
  Group,
  Level,
  LocationFindModalResult,
  Position,
  PositionCardModalResult,
  PositionedDeviceData,
  Zone
} from 'src/app/model';
import { ModalDeviceComponent } from 'src/app/modals/device-card/device-card.modal';
import { DeviceEditComponent } from 'src/app/modals/device-edit/device-edit.modal';
import { PositionCardModalComponent } from 'src/app/modals/position-card/position-card.modal';
import { PositionEditModalComponent } from 'src/app/modals/position-edit/position-edit.modal';
import { DeviceBindModalComponent } from 'src/app/modals/device-bind/device-bind.modal';
import { DeviceFindModalComponent } from 'src/app/modals/device-find/device-find.modal';
import { SettingsModalComponent } from 'src/app/modals/settings/settings.modal';
import { BindSwiperModalComponent } from 'src/app/modals/bind/bind.modal';
import { LocationFindModalComponent } from 'src/app/modals/location-find/location-find.modal';
import { CommonModule } from '@angular/common';
import { MapsService } from '../providers/maps/maps.service';
import { FormsModule } from '@angular/forms';
import { ProjectSelectPaneComponent } from '../components/project-select-pane/project-select-pane.component';

@Component({
  selector: 'app-home',
  templateUrl: 'home.page.html',
  styleUrls: ['home.page.scss'],
  standalone: true,
  imports: [IonicModule, CommonModule, FormsModule, ProjectSelectPaneComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class HomePage implements OnInit, OnDestroy {
  @ViewChild('map') mapRef!: ElementRef<HTMLElement>;
  @ViewChild('fab') fab!: IonFab;
  @ViewChild('mainFab') mainFab!: IonFab;
  public newMap: GoogleMap | null = null;

  public selectedMapType: MapType;
  public mapTypes = mapTypes;

  private deviceSub: Subscription;
  private positionSub: Subscription;
  private accurateSub: Subscription;
  private accurateDeviceSub: Subscription;
  private mapTypeSub: Subscription;

  public accurateGroupPositions$ = this.groupBindStore.accuratePositions$;
  public accurateDevicePosition$ = this.deviceBindStore.accurateDevicePosition$;
  public pointsPositions$ = this.groupBindStore.pointsPositions$;

  public loading$ = this.dmStoreService.loading$;

  public native: boolean;

  public settingsSide = 'bottom';

  constructor(
    private modalController: ModalController,
    private dmStoreService: DmStoreService,
    private groupBindStore: GroupBindStoreService,
    private helper: HelperProvider,
    private deviceBindStore: DeviceBindStoreService,
    private api: ApiService,
    private ngZone: NgZone,
    private localStorageService: LocaLstorageService,
    private maps: MapsService,
  ) { }

  async ngOnInit() {
    this.deviceSub = this.dmStoreService.chosenDevice$.subscribe(async device => device && await this.deviceCard(device));
    this.positionSub = this.dmStoreService.chosenPosition$.subscribe(async position => position && await this.positionCard(position));
    this.mapTypeSub = this.localStorageService.mapType$.subscribe(map => this.selectedMapType = map);
    this.deviceBindStore.openBindFromUrl$.subscribe(val => val && this.openBind());

    ScreenOrientation.addListener('screenOrientationChange', (orientation) => {
      if (orientation.type === 'landscape-primary') {
        this.settingsSide = 'start';
      } else {
        this.settingsSide = 'bottom';
      }
    });

    const orientation = await ScreenOrientation.orientation();
    if (orientation.type === 'landscape-primary') {
      this.settingsSide = 'start';
    } else {
      this.settingsSide = 'bottom';
    }
  }

  ionViewDidEnter() {
    this.maps.init({ lat: 52.032750, lng: -1.198194 }, this.mapRef.nativeElement);
  }

  ngOnDestroy() {
    this.deviceSub?.unsubscribe();
    this.positionSub?.unsubscribe();
    this.accurateSub?.unsubscribe();
    this.accurateDeviceSub?.unsubscribe();
    this.mapTypeSub?.unsubscribe();
    ScreenOrientation.removeAllListeners();
  }


  private async deviceCard(device: PositionedDeviceData) {
    if (this.groupBindStore.accuratePositions || this.deviceBindStore.accurateDevicePosition) {
      return;
    }

    if (device.lat && device.lon) {
      this.maps.bound([{ lat: device.lat, lon: device.lon }]);
    }

    const modal = await this.modalController.create({
      component: ModalDeviceComponent,
      componentProps: { device },
      cssClass: 'auto-height top',
    });

    await modal.present();

    const { data } = await modal.onDidDismiss<DeviceCardModalResult>();

    if (data?.edit) {
      const { zone, group, level } = data.edit;
      this.deviceEdit(device, zone, group, level);
    }
  }

  private async positionCard(position: Position) {
    if (this.groupBindStore.accuratePositions) {
      return;
    }

    if (this.deviceBindStore.accurateDevicePosition) {
      this.maps.setCenter(position.lat, position.lon, 22);
      this.ngZone.run(() => this.deviceBindStore.existingPositionForBind = position.id);
      this.helper.confirm(
        this.bindAction.bind(this),
        'Are you sure you want to bind device to this position?',
        'Confirmation',
        this.cancelBind.bind(this),
      );
      return;
    }

    this.maps.bound([{ lat: position.lat, lon: position.lon }]);

    const modal = await this.modalController.create({
      component: PositionCardModalComponent,
      componentProps: { position },
      cssClass: 'auto-height top',
    });

    await modal.present();

    const { data } = await modal.onDidDismiss<PositionCardModalResult>();

    if (data?.edit) {
      this.positionEdit(position, data.additionalData);
    }

    if (data?.bind) {
      this.deviceBind(position, data.additionalData);
    }
  }

  private async positionEdit(position: Position, { zone, group, level }: PositionCardModalResult['additionalData'] = {}) {
    const modal = await this.modalController.create({
      component: PositionEditModalComponent,
      componentProps: { position, zone, group, level },
    });

    await modal.present();

    const { data } = await modal.onDidDismiss<PositionCardModalResult>();

    if (data?.deleted) {
      this.helper.showToast('Position has been deleted successfully');
    }

    if (data?.binded) {
      this.helper.showToast('Device has been bound successfully');
      sendSuccessTodnwaveio({ device: data?.binded });
    }
  }

  private async deviceEdit(device: PositionedDeviceData, zone: Zone, group: Group, level?: Level) {
    const edit = await this.modalController.create({
      component: DeviceEditComponent,
      componentProps: { device, zone, group, level },
    });

    await edit.present();

    const { data } = await edit.onDidDismiss<DeviceCardModalResult>();

    if (data?.unbind) {
      this.helper.showToast('Device has been unbound successfully');
      sendSuccessTodnwaveio({ device: device.device_id });
    }
  }

  private async deviceBind(position: Position, { zone, group, level }: PositionCardModalResult['additionalData'] = {}) {
    const bind = await this.modalController.create({
      component: DeviceBindModalComponent,
      componentProps: { position, zone, group, level },
    });

    await bind.present();

    const { data } = await bind.onDidDismiss<PositionCardModalResult>();

    if (data?.deleted) {
      this.helper.showToast('Position has been deleted successfully');
    }

    if (data?.binded) {
      this.helper.showToast('Device has been bound successfully');
      sendSuccessTodnwaveio({ device: data?.binded });
    }
  }

  public async openSearch(e?: Event) {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const modal = await this.modalController.create({
      component: DeviceFindModalComponent,
      cssClass: 'auto-height',
    });

    this.keepFabOpens();

    modal.present();

    const { data } = await modal.onDidDismiss<DeviceFindModalResult>();
    if (data) {
      this.dmStoreService.selectFoundDevice(data.device);
    }
  }

  public async openSettings(e?: Event) {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    this.keepFabOpens();

    const settings = await this.modalController.create({
      component: SettingsModalComponent,
    });

    await settings.present();
  }

  public async openBind(e?: Event) {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const modal = await this.modalController.create({
      component: BindSwiperModalComponent,
    });

    modal.present();

    this.keepFabOpens();

    const { data } = await modal.onDidDismiss<BindModalResult>();

    if (data?.accurate) {
      this.groupBindStore.accuratePositions = true;
    }
  }

  private keepFabOpens() {
    if (this.fab) {
      setTimeout(() => this.fab.activated = true, 100);
    }
    if (this.mainFab) {
      setTimeout(() => this.mainFab.activated = true, 100);
    }
  }

  public async myLocation(e?: Event) {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    this.keepFabOpens();

    const latlon = await this.maps.getCurrentPosition();

    if (!latlon || isNil(latlon?.lat)) {
      this.helper.showToast('Your location is unavailable. Turn on GPS');
      return;
    }

    this.maps.setCenter(latlon.lat, latlon.lon, 20);
  }

  private async bindAction() {
    const positionId = this.deviceBindStore.existingPositionForBind;
    const deviceId = this.deviceBindStore.device?.device_id;

    if (!deviceId || !positionId) {
      return;
    }

    this.helper.showLoading();
    const result = await this.api.devices.bindDeviceToPosition(deviceId, { position_id: positionId });

    if (!result.error) {
      this.dmStoreService.bindDevice(deviceId, positionId);
      this.helper.showToast('Device has been bound successfully');
      sendSuccessTodnwaveio({ device: deviceId });
      this.deviceBindStore.accurateDevicePosition = false;
    } else {
      this.helper.showToast('Can\'t bind device');
    }

    this.helper.hideLoading();
  }

  private cancelBind() {
    const mapCenter = this.maps.getCenter();
    const moved = this.maps.utils.moveMarker([{ lat: mapCenter.lat, lon: mapCenter.lng }], 90);
    this.maps.setCenter(moved[0].lat, moved[0].lon);
    this.deviceBindStore.existingPositionForBind = undefined;
  }

  public async openSearchLocation(e?: Event) {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    this.keepFabOpens();

    const modal = await this.modalController.create({
      component: LocationFindModalComponent,
      cssClass: 'auto-height keyboard-fix',
    });

    modal.present();

    await modal.onDidDismiss<LocationFindModalResult>();
  }

  public changeMapType() {
    if (this.selectedMapType === this.mapTypes[0].value) {
      this.selectedMapType = this.mapTypes[1].value;
    } else {
      this.selectedMapType = this.mapTypes[0].value;
    }

    this.localStorageService.mapType = this.selectedMapType;
  }

}
